import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageHeader from "./ui/PageHeader";
import SectionHead from "./ui/SectionHead";
import { ArrowUpRight } from "./ui/Icons";

import bandImg from "../image/growing-potatoes-main.jpg";
import coverageImg from "../image/tomato-greenhouse-farming.jpg";

const CAPABILITIES = [
  { titleKey: "sections.diseaseDetection.title", descKey: "sections.diseaseDetection.description" },
  { titleKey: "sections.precisionAgriculture.title", descKey: "sections.precisionAgriculture.description" },
  { titleKey: "sections.enterpriseFeatures.title", descKey: "sections.enterpriseFeatures.description" },
  { titleKey: "sections.localization.title", descKey: "sections.localization.description" },
  { titleKey: "sections.modelExpansion.title", descKey: "sections.modelExpansion.description" },
  { titleKey: "sections.futureGoals.title", descKey: "sections.futureGoals.description" },
];

/* The seven crops the models actually cover. Set as a typographic index —
   pairing each name with a stock photo would mislabel produce we don't have
   photography for. */
const CROPS = [
  "Potato",
  "Tomato",
  "Maize",
  "Pepper",
  "Beans",
  "Cassava",
  "Apple",
];

const AgriHyphenAIExplanation = () => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-agri-paper">
      <PageHeader
        kicker="About the platform"
        title={t("title1")}
        lede={t("subtitle")}
      />

      {/* Full-bleed band anchors the page before the reading starts */}
      <div className="relative isolate h-[18rem] overflow-hidden bg-agri-forestDeep sm:h-[24rem] lg:h-[28rem]">
        <img
          src={bandImg}
          alt="A potato harvest lifted from the soil beside a row of healthy plants"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 photo-scrim-soft" />
      </div>

      {/* Capability register */}
      <section className="section-tight container-x">
        <SectionHead kicker="What it does" title="Capabilities">
          Six areas of work, from the vision models in the field to the
          languages the recommendations are delivered in.
        </SectionHead>

        <div className="grid grid-cols-1 gap-x-14 lg:grid-cols-2">
          {CAPABILITIES.map((item, i) => (
            <article
              key={item.titleKey}
              /* In two columns the second item also starts a column, so it
                 loses its top rule too — computed rather than expressed as a
                 stacked `lg:nth-child` variant. */
              className={[
                "grid grid-cols-[3rem_1fr] gap-x-4 border-t border-agri-rule py-7 sm:gap-x-6",
                i === 0 ? "border-t-0 pt-0" : "",
                i === 1 ? "lg:border-t-0 lg:pt-0" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="pt-1 font-mono text-xs font-medium tabular-nums text-agri-clay">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-[1.1875rem] font-semibold leading-snug tracking-[-0.015em] text-agri-ink">
                  {t(item.titleKey)}
                </h3>
                <p className="mt-2.5 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
                  {t(item.descKey)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Crop coverage */}
      <section className="section-tight border-t border-agri-rule bg-agri-card">
        <div className="container-x">
          <SectionHead kicker="Coverage" title="Seven crops, thirty-seven diseases">
            Each crop has its own trained model, so a diagnosis is made against
            the diseases that actually affect that plant.
          </SectionHead>

          <div className="grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-12">
            {/* Crop index, set as a numbered list rather than seven cards */}
            <ol className="lg:col-span-6">
              {CROPS.map((crop, i) => (
                <li
                  key={crop}
                  className="flex items-baseline gap-5 border-b border-agri-rule py-4 last:border-b-0"
                >
                  <span className="font-mono text-[0.6875rem] font-medium tabular-nums text-agri-clay">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-[1.375rem] font-semibold tracking-[-0.02em] text-agri-ink sm:text-[1.625rem]">
                    {crop}
                  </span>
                </li>
              ))}
            </ol>

            <figure className="lg:col-span-6">
              <img
                src={coverageImg}
                alt="A farmer harvesting healthy tomatoes into crates"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover lg:aspect-[3/4]"
              />
              <figcaption className="mt-4 border-t border-agri-rule pt-3 font-mono text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-agri-ink40">
                Tomato — one of seven crops with a dedicated detection model
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="border-t border-agri-rule bg-agri-paper">
        <div className="container-x flex flex-col items-start gap-6 py-14 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-measure text-balance font-display text-[1.5rem] font-semibold leading-tight tracking-[-0.02em] text-agri-ink sm:text-[1.75rem]">
            Point a phone at a leaf and see what it finds.
          </p>
          <Link to="/test-ai" className="btn-primary shrink-0">
            {t("testOurAI")}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AgriHyphenAIExplanation;
